import React, { useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomButton from '@/components/CustomButton'
import { toFixedNoRound, formatThousands } from '@/utils'
import { RecipientType } from '../index.type'

interface TransferSuccessData {
  currency: string
  currencyIcon: string
  amount: string
  recipientType: RecipientType
  recipientValue: string
  fee: string
  timestamp: string
  // 以下为模拟的交易详情数据
  txHash?: string
  from?: string
  to?: string
  gasFee?: string
  blockchainFee?: string
  transactionId?: string
}

export default React.memo(function TransferSuccess() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  // 获取转账成功数据
  const successData = useMemo(() => {
    const state = location.state as TransferSuccessData
    return state
  }, [location.state])

  // 如果没有数据，返回转账页面
  if (!successData) {
    navigate('/transfer', { replace: true })
    return null
  }

  // 格式化时间
  const formattedTime = useMemo(() => {
    const date = new Date(successData.timestamp)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
  }, [successData.timestamp])

  // 生成模拟的交易数据
  const transactionDetails = useMemo(() => {
    return {
      from: successData.from || '0x134223417254123513f1',
      to: successData.recipientValue || '',
      gasFee: successData.gasFee || '',
      blockchainFee: successData.blockchainFee || '',
      transactionId: successData.transactionId || '',
      date: formattedTime
    }
  }, [successData, formattedTime])

  // 完成按钮
  const handleComplete = useCallback(() => {
    navigate('/home', { replace: true })
  }, [navigate])

  return (
    <PageLayout>
      <Header
        centerChildren={<span className="text-3.75 font-medium text-black">{t('转账')}</span>}
      />

      <div className="bg-white px-6 py-6 flex flex-col pb-24">
        {/* 成功图标 */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-[#00D89D] flex items-center justify-center">
            <img src="/icon/transfer-success.svg" alt="success" className="w-8 h-8" />
          </div>
        </div>

        {/* 状态标题 */}
        <div className="text-center text-4.5 font-medium text-black mb-2">SUCCEEDED</div>

        {/* 金额 */}
        <div className="text-center mb-5">
          <span className="text-8 font-bold text-black">-{formatThousands(successData.amount, 2, true)}</span>
          <span className="text-8 text-black font-bold ml-2">{successData.currency}</span>
        </div>

        {/* 已授权 */}
        {/* <div className="text-center text-3.5 text-[#979797] mb-8">{t('已授权')}</div> */}

        {/* 付款服务卡片 */}
        <div className="px-4.5 py-4 rounded-2xl bg-white border-[0.5px] border-[#000000]/10 border-solid space-y-3 mb-3">
          <div className="justify-between items-center flex">
            <div className="text-xs text-[#000000]/70">{t('付款服务')}</div>
            <div className="text-xs text-[#000000]">{successData.currency}</div>
          </div>
          <div className="justify-between items-center flex">
            <div className="text-xs text-[#000000]/70">{t('金额')}</div>
            <div className="text-xs text-[#000000]">-{formatThousands(successData.amount, 2, true)} USD</div>
          </div>
        </div>

        {/* 交易详情卡片 */}
        <div className="px-4.5 py-4 rounded-2xl bg-white border-[0.5px] border-[#000000]/10 border-solid space-y-3 mb-10">
          <div className="text-xs font-600 text-[#000000]/70">{t('交易详情')}</div>
          
          {/* 从 */}
          {/* <div className="justify-between items-center flex">
            <div className="text-xs text-[#000000]/70">{t('从')}</div>
            <div className="text-xs text-[#000000] text-right break-all ml-4 max-w-[60%]">
              {transactionDetails.from}
            </div>
          </div> */}

          {/* 到 */}
          <div className="justify-between items-center flex">
            <div className="text-xs text-[#000000]/70">{t('到')}</div>
            <div className="text-xs text-[#000000] text-right break-all ml-4 max-w-[60%]">
              {transactionDetails.to}
            </div>
          </div>

          {/* 授权费 */}
          {/* <div className="justify-between items-center flex">
            <div className="text-xs text-[#000000]/70">{t('授权费')}</div>
            <div className="text-xs text-[#000000]">{transactionDetails.gasFee}</div>
          </div> */}

          {/* 加密货币区块链费用 */}
          {/* <div className="justify-between items-center flex">
            <div className="text-xs text-[#000000]/70">{t('加密货币区块链费用')}</div>
            <div className="text-xs text-[#000000]">{transactionDetails.blockchainFee}</div>
          </div> */}

          {/* 日期 */}
          <div className="justify-between items-center flex">
            <div className="text-xs text-[#000000]/70">{t('日期')}</div>
            <div className="text-xs text-[#000000]">{transactionDetails.date}</div>
          </div>

          {/* 交易ID */}
          {/* <div className="justify-between items-center flex">
            <div className="text-xs text-[#000000]/70">{t('交易ID')}</div>
            <div className="text-xs text-[#000000] text-right break-all ml-4 max-w-[60%]">
              {transactionDetails.transactionId}
            </div>
          </div> */}
        </div>

        {/* 完成按钮 */}
        <div className="flex justify-center">
          <CustomButton onClick={handleComplete}>
            {t('完成')}
          </CustomButton>
        </div>
      </div>
    </PageLayout>
  )
})
